import React, { useState } from "react";

import {
  Button,
  FormControlLabel,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";

import SettingsTitle from "@app/analytics/account/components/SettingsTitle";
import { SettingsModuleProps } from "@app/analytics/account/types";
import {
  botNameValidationSchema,
  DEFAULT_BOT_NAME_MODE,
  MAX_BOT_NAME_LENGTH,
  MIN_BOT_NAME_LENGTH,
} from "@app/analytics/account/utils";
import WorkspaceCanEditDropdown from "@app/analytics/account/WorkspaceSettings/components/WorkspaceCanEditDropdown";
import {
  ASSISTANT_NAME_CONFIG,
  ASSISTANT_DISPLAY_CONFIG,
} from "@app/analytics/account/WorkspaceSettings/constants";
import Radio from "@app/analytics/components/Radio";
import TextField from "@app/analytics/components/TextField";
import UpsellTooltipWrapper from "@app/analytics/components/UpsellTooltipWrapper";
import { BotNameMode } from "@app/analytics/types";
import { TOOLTIPS } from "@app/common/content";

import { DefaultBadge } from "@rui/Badge";

const ReadAssistantNameSection = ({
  settings,
  onSettingUpdate,
  workspaceIsInactive,
}: SettingsModuleProps) => {
  const [customName, setCustomName] = useState<string | null>(null);
  const [customNameError, setCustomNameError] = useState<string>("");
  const [botNameMode, setBotNameMode] = useState<BotNameMode | null>(null);

  const selectedBotNameMode =
    botNameMode ?? settings.botNameMode ?? DEFAULT_BOT_NAME_MODE;
  const customBotName = customName ?? settings.botNameModeCustomName ?? "";

  const handleCanUpdateBotNameModeChange = (
    botNameModeIsUserEditable: boolean
  ) => onSettingUpdate({ botNameModeIsUserEditable });

  const handleCustomNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (customNameError) setCustomNameError("");
    setCustomName(event.target.value);
  };

  const handleBotNameModeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newMode = event.currentTarget.value as BotNameMode;
    setBotNameMode(newMode);

    // if custom, we don't want to update unless there is a name to update to
    if (newMode === "custom" && !customBotName) return;

    onSettingUpdate({
      botNameMode: newMode,
      botNameModeCustomName: customBotName,
    });
  };

  const handleCustomNameSave = async () => {
    const isValidBotName = await botNameValidationSchema.isValid({
      botNameModeCustomName: customName,
    });

    if (!isValidBotName) {
      setCustomNameError(
        `Name must be between ${MIN_BOT_NAME_LENGTH} and ${MAX_BOT_NAME_LENGTH} characters.`
      );
      return;
    }

    return onSettingUpdate({
      botNameModeCustomName: customName!,
      botNameMode: selectedBotNameMode,
    });
  };

  return (
    <>
      <SettingsTitle
        title={ASSISTANT_NAME_CONFIG.title}
        subtitle={ASSISTANT_NAME_CONFIG.subtitle}
      />
      <WorkspaceCanEditDropdown
        disabled={workspaceIsInactive}
        onUpdate={handleCanUpdateBotNameModeChange}
        selectedValue={settings.botNameModeIsUserEditable}
        name={ASSISTANT_DISPLAY_CONFIG.name}
        tooltip={workspaceIsInactive ? TOOLTIPS.upsell.upgradeToPro : undefined}
      />
      <Stack>
        <UpsellTooltipWrapper show={workspaceIsInactive} placement="right">
          <RadioGroup
            value={selectedBotNameMode}
            onChange={handleBotNameModeChange}
          >
            {ASSISTANT_NAME_CONFIG.options.map((option) => (
              <Stack key={option.id} direction="row" alignItems="center">
                <FormControlLabel
                  control={
                    <Radio value={option.id} disabled={workspaceIsInactive} />
                  }
                  label={
                    <Typography
                      variant="bodySecondaryReg"
                      sx={{ color: "shades.neutral.600" }}
                    >
                      {option.displayName()}
                    </Typography>
                  }
                />
                {option.default && <DefaultBadge />}
              </Stack>
            ))}
          </RadioGroup>
        </UpsellTooltipWrapper>
        {selectedBotNameMode === "custom" && !workspaceIsInactive && (
          <Stack direction="row" spacing={1} sx={{ height: "44px", my: 1 }}>
            <TextField
              sx={{ width: "360px" }}
              InputProps={{
                placeholder: "Type custom assistant name...",
                value: customBotName,
              }}
              onChange={handleCustomNameChange}
              error={Boolean(customNameError)}
              helperText={customNameError}
            />
            <Button
              variant="contained"
              onClick={handleCustomNameSave}
              sx={{ height: "40px", width: "120px" }}
            >
              Save
            </Button>
          </Stack>
        )}
      </Stack>
    </>
  );
};

export default ReadAssistantNameSection;
